import React, { useState, useEffect } from 'react';
import { useUser } from '@/contexts/UserContext';
import { apiRequest } from '@/services/queryClient';
import { getApiBaseUrl } from '@/config/production';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    ShoppingBag,
    CreditCard,
    CheckCircle,
    Clock,
    XCircle,
    Download,
    Eye,
    Package,
    Truck,
    AlertCircle,
    Loader2,
    ExternalLink,
    Copy,
    ArrowLeft
} from 'lucide-react';
import { format } from 'date-fns';
import { Link, useRoute } from 'wouter';
import { StripeService } from '@/services/stripe.service';

interface OrderItem {
    id: string;
    photoid: string;
    data: {
        photo: {
            id: string;
            filename: string;
            original_filename: string;
            storageurl: string;
        };
        event: {
            title: string;
            author: {
                name: string;
                email: string;
            };
        };
    };
    quantity: number;
    unitprice: number;
    photo?: {
        id: string;
        filename: string;
        original_filename: string;
        storageurl: string;
        photographer?: {
            id: string;
            name: string;
            email: string;
        };
        photo_events?: Array<{
            event: {
                title: string;
            };
        }>;
    };
}

interface Payment {
    id: string;
    provider: string;
    amount: string;
    status: string;
    providerreference: string;
    createdat: string;
    completedat?: string;
}

interface StripeInfo {
    sessionId?: string;
    paymentIntentId?: string;
    customerEmail?: string;
    paymentMethod?: string;
    receiptUrl?: string;
    invoiceUrl?: string;
}

interface Order {
    id: string;
    userid: string;
    eventid: string | null;
    totalamount: string;
    status: string;
    createdat: string;
    ordernumber: number;
    items: OrderItem[];
    payments: Payment[];
}

const statusSteps = [
    { key: 'created', label: 'Pedido Emitido', icon: Package, color: 'bg-primary' },
    { key: 'pending', label: 'Aguardando Pagamento', icon: Clock, color: 'bg-yellow-500' },
    { key: 'paid', label: 'Pagamento Concluído', icon: CheckCircle, color: 'bg-primary' },
    { key: 'completed', label: 'Download Liberado', icon: Download, color: 'bg-purple-500' }
];

export default function OrderStatusPage() {
    const { user, isLoggedIn, isLoading: isUserLoading } = useUser();
    const [, params] = useRoute('/order-status/:orderId');
    const [order, setOrder] = useState<Order | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
    const [stripeInfo, setStripeInfo] = useState<StripeInfo | null>(null);
    const [isLoadingStripe, setIsLoadingStripe] = useState(false);

    const orderId = params?.orderId;

    const getStatusStep = (order: Order) => {
        if (order.status === 'Paid') {
            return order.payments && order.payments.length > 0 && order.payments.some(p => p.status === 'Completed') ? 'completed' : 'paid';
        }
        if (order.status === 'Pending') {
            return order.payments && order.payments.length > 0 ? 'pending' : 'created';
        }
        return 'created';
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Paid': return 'bg-primary hover:bg-secondary';
            case 'Pending': return 'bg-yellow-500 hover:bg-yellow-600';
            case 'Cancelled': return 'bg-red-500 hover:bg-red-600';
            default: return 'bg-gray-500 hover:bg-gray-600';
        }
    };

    const formatDate = (dateString: string) => {
        return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
    };

    const formatCurrency = (amount: string | number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(typeof amount === 'string' ? parseFloat(amount) : amount);
    };

    const fetchOrder = async () => {
        if (!orderId) return;

        try {
            setIsLoading(true);
            setError(null);

            const API_BASE = `${getApiBaseUrl()}/api`;
            const response = await apiRequest('GET', `${API_BASE}/orders/${orderId}`);
            const data = await response.json();

            if (data.success) {
                const orderData = data.data;

                // Mapear os dados corretamente
                const mappedOrder = {
                    ...orderData,
                    items: orderData.items || [], // items vem do backend
                    payments: orderData.payments || [] // payments vem do backend
                };

                setOrder(mappedOrder);

                // Buscar informações do Stripe se houver pagamento
                if (mappedOrder.payments && mappedOrder.payments.length > 0) {
                    await fetchStripeInfo(mappedOrder.payments[0]);
                }
            } else {
                setError('Erro ao carregar pedido');
            }
        } catch (err) {
            console.error('Erro ao buscar pedido:', err);
            setError('Erro ao carregar pedido');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchStripeInfo = async (payment: Payment) => {
        if (!payment.providerreference) return;

        try {
            setIsLoadingStripe(true);

            // Tentar buscar como session primeiro
            try {
                const sessionData = await StripeService.getCheckoutSession(payment.providerreference);
                if (sessionData.success) {
                    setStripeInfo({
                        sessionId: payment.providerreference,
                        customerEmail: sessionData.data.customer_email,
                        paymentMethod: sessionData.data.payment_method_types?.[0],
                        receiptUrl: sessionData.data.receipt_url,
                        invoiceUrl: sessionData.data.invoice_url
                    });

                    return;
                }
            } catch (sessionError) {
                console.log('⚠️ [OrderStatus] Não é uma session, tentando como PaymentIntent...');
            }

            // Tentar buscar como PaymentIntent
            try {
                const paymentIntentData = await StripeService.getPaymentIntent(payment.providerreference);
                if (paymentIntentData.success) {
                    setStripeInfo({
                        paymentIntentId: payment.providerreference,
                        customerEmail: paymentIntentData.data.receipt_email,
                        paymentMethod: paymentIntentData.data.payment_method?.type,
                        receiptUrl: paymentIntentData.data.receipt_url
                    });

                }
            } catch (paymentIntentError) {
                console.log('⚠️ [OrderStatus] Não é um PaymentIntent válido');
            }

        } catch (error) {
            console.error('❌ [OrderStatus] Erro ao buscar informações do Stripe:', error);
        } finally {
            setIsLoadingStripe(false);
        }
    };

    const handleDownload = async (photoId: string, filename: string) => {
        try {
            const API_BASE = `${getApiBaseUrl()}/api`;
            const downloadUrl = `${API_BASE}/storage/download/${photoId}?userId=${user?.id}`;

            window.open(downloadUrl, '_blank');
        } catch (err) {
            console.error('Erro ao fazer download:', err);
            alert('Erro ao fazer download da foto');
        }
    };

    const handleViewPhoto = (photoUrl: string) => {
        setSelectedPhoto(photoUrl);
    };

    useEffect(() => {
        if (isLoggedIn && user?.id && orderId) {
            fetchOrder();
        } else if (!isUserLoading) {
            setIsLoading(false);
            setError('Você precisa estar logado para visualizar o status do pedido.');
        }
    }, [isLoggedIn, user?.id, orderId, isUserLoading]);

    if (!isLoggedIn) {
        return (
            <div className="container mx-auto px-4 py-8">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-red-500" />
                            Acesso Negado
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p>Por favor, faça login para visualizar o status do pedido.</p>
                        <Button asChild className="mt-4">
                            <Link to="/login">Fazer Login</Link>
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="container mx-auto px-4 py-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="mt-2">Carregando status do pedido...</p>
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="container mx-auto px-4 py-8">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-red-500" />
                            Erro
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p>{error || 'Pedido não encontrado'}</p>
                        <Button onClick={fetchOrder} className="mt-4">
                            Tentar Novamente
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const currentStep = getStatusStep(order);
    const canDownload = order.status === 'Paid' && order.payments && order.payments.length > 0 && order.payments.some(p => p.status === 'Completed');

    return (
        <div className="container mx-auto px-4 py-8">
            {/* Cabeçalho do Pedido */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            Status do Pedido #{order.ordernumber || order.id.substring(0, 8)}
                        </h1>
                        <p className="text-gray-600">
                            Acompanhe o progresso do seu pedido
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        asChild
                        className="flex items-center gap-2"
                    >
                        <Link to="/orders">
                            <ArrowLeft className="h-4 w-4" />
                            Voltar para Pedidos
                        </Link>
                    </Button>
                </div>
            </div>

            {/* Timeline de Status */}
            <Card className="mb-8">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Package className="h-5 w-5" />
                        Progresso do Pedido
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-between">
                        {statusSteps.map((step, index) => {
                            const isActive = currentStep === step.key;
                            const isCompleted = statusSteps.findIndex(s => s.key === currentStep) > index;
                            const Icon = step.icon;

                            return (
                                <div key={step.key} className="flex flex-col items-center">
                                    <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center mb-2
                    ${isActive ? step.color : isCompleted ? 'bg-primary' : 'bg-gray-300'}
                    text-white
                  `}>
                                        <Icon className="h-6 w-6" />
                                    </div>
                                    <span className={`text-sm font-medium ${isActive ? 'text-gray-900' : isCompleted ? 'text-primary' : 'text-gray-500'}`}>
                                        {step.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Detalhes do Pedido */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Informações do Pedido */}
                <Card>
                    <CardHeader>
                        <CardTitle>Informações do Pedido</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Número do Pedido:</span>
                            <span className="font-semibold">#{order.ordernumber || order.id.substring(0, 8)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Data:</span>
                            <span>{formatDate(order.createdat)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Status:</span>
                            <Badge className={getStatusColor(order.status)}>
                                {order.status === 'Paid' ? 'Pago' : order.status === 'Pending' ? 'Pendente' : 'Cancelado'}
                            </Badge>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Total:</span>
                            <span className="font-semibold text-lg">{formatCurrency(order.totalamount)}</span>
                        </div>
                    </CardContent>
                </Card>

                {/* Informações de Pagamento */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center space-x-2">
                            <CreditCard className="h-5 w-5" />
                            <span>Informações de Pagamento</span>
                            {isLoadingStripe && <Loader2 className="h-4 w-4 animate-spin" />}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {order.payments && order.payments.length > 0 ? (
                            order.payments.map((payment, index) => (
                                <div key={payment.id} className="border rounded-lg p-4 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm font-medium">Provedor:</span>
                                                <Badge variant="outline" className="capitalize">
                                                    {payment.provider}
                                                </Badge>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm font-medium">Valor:</span>
                                                <span className="font-semibold text-lg">{formatCurrency(payment.amount)}</span>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm font-medium">Status:</span>
                                                <Badge className={payment.status === 'Completed' ? 'bg-primary' : 'bg-yellow-500'}>
                                                    {payment.status === 'Completed' ? 'Pago' : 'Pendente'}
                                                </Badge>
                                            </div>
                                            {payment.completedat && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium">Pago em:</span>
                                                    <span className="text-sm text-gray-600">
                                                        {formatDate(payment.completedat)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Informações do Stripe */}
                                    {stripeInfo && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                                            <div className="flex items-center space-x-2 mb-3">
                                                <ExternalLink className="h-4 w-4 text-blue-600" />
                                                <span className="text-sm font-medium text-blue-800">Informações do Stripe</span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {stripeInfo.customerEmail && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm font-medium">Email do Cliente:</span>
                                                        <span className="text-sm text-gray-600">{stripeInfo.customerEmail}</span>
                                                    </div>
                                                )}

                                                {stripeInfo.paymentMethod && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm font-medium">Método de Pagamento:</span>
                                                        <Badge variant="outline" className="capitalize">
                                                            {stripeInfo.paymentMethod}
                                                        </Badge>
                                                    </div>
                                                )}

                                                {stripeInfo.sessionId && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm font-medium">Session ID:</span>
                                                        <span className="text-xs font-mono text-gray-600">
                                                            {stripeInfo.sessionId.substring(0, 20)}...
                                                        </span>
                                                    </div>
                                                )}

                                                {stripeInfo.paymentIntentId && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm font-medium">Payment Intent:</span>
                                                        <span className="text-xs font-mono text-gray-600">
                                                            {stripeInfo.paymentIntentId.substring(0, 20)}...
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex gap-2 pt-2">
                                                {stripeInfo.receiptUrl && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => window.open(stripeInfo.receiptUrl, '_blank')}
                                                        className="flex items-center gap-2"
                                                    >
                                                        <ExternalLink className="h-3 w-3" />
                                                        Recibo
                                                    </Button>
                                                )}

                                                {stripeInfo.invoiceUrl && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => window.open(stripeInfo.invoiceUrl, '_blank')}
                                                        className="flex items-center gap-2"
                                                    >
                                                        <ExternalLink className="h-3 w-3" />
                                                        Fatura
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {payment.providerreference && (
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-sm font-medium">ID da Transação:</span>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(payment.providerreference);
                                                        // Aqui você pode adicionar um toast de sucesso
                                                    }}
                                                    className="flex items-center gap-2"
                                                >
                                                    <Copy className="h-3 w-3" />
                                                    Copiar
                                                </Button>
                                            </div>
                                            <span className="text-xs font-mono text-gray-600 break-all">
                                                {payment.providerreference}
                                            </span>
                                        </div>
                                    )}

                                    {payment.status === 'Completed' && (
                                        <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
                                            <div className="flex items-center space-x-2">
                                                <CheckCircle className="h-4 w-4 text-primary" />
                                                <span className="text-sm font-medium text-foreground">
                                                    Pagamento confirmado e processado com sucesso
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {payment.status === 'Pending' && (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                            <div className="flex items-center space-x-2">
                                                <Clock className="h-4 w-4 text-yellow-600" />
                                                <span className="text-sm font-medium text-yellow-800">
                                                    Aguardando confirmação do pagamento
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    <div className="text-sm text-gray-600">
                                        <p>Criado: {formatDate(payment.createdat)}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8">
                                <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">Nenhuma informação de pagamento disponível.</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Itens do Pedido */}
            <Card className="mt-8">
                <CardHeader>
                    <CardTitle>Itens do Pedido</CardTitle>
                </CardHeader>
                <CardContent>
                    {order.items && order.items.length > 0 ? (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-16">Foto</TableHead>
                                        <TableHead>Nome do Arquivo</TableHead>
                                        <TableHead>Evento</TableHead>
                                        <TableHead>Fotógrafo</TableHead>
                                        <TableHead className="text-center">Qtd</TableHead>
                                        <TableHead className="text-right">Preço Unit.</TableHead>
                                        <TableHead className="text-right">Total</TableHead>
                                        <TableHead className="text-center">Ações</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {order.items.map((item, index) => (
                                        <TableRow key={item.id}>
                                            <TableCell>
                                                <img
                                                    src={item.data.photo?.storageurl}
                                                    alt={item.data.photo?.original_filename || `Foto ${item.data.photo?.id.slice(-8) || 'N/A'}`}
                                                    className="w-12 h-12 object-cover rounded-lg"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <div className="max-w-xs">
                                                    <p className="font-medium text-sm truncate">
                                                        {item.data.photo?.original_filename || `Foto ${item.data.photo?.id.slice(-8) || 'N/A'}`}
                                                    </p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-primary">
                                                    {item.data?.event?.title || 'Evento não especificado'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-primary">
                                                    {item.data.event.author?.name || 'Não especificado'}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <span className="font-medium">{item.quantity}</span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <span className="text-sm">{formatCurrency(item.unitprice)}</span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <span className="font-semibold">
                                                    {formatCurrency(item.unitprice * item.quantity)}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1 justify-center">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleViewPhoto(item.data.photo?.storageurl || '')}
                                                        className="h-8 w-8 p-0"
                                                        title="Visualizar foto"
                                                    >
                                                        <Eye className="h-3 w-3" />
                                                    </Button>

                                                    {canDownload && (
                                                        <Button
                                                            size="sm"
                                                            onClick={() => handleDownload(
                                                                item.data.photo?.id || '',
                                                                item.data.photo?.original_filename || 'foto.jpg'
                                                            )}
                                                            className="h-8 w-8 p-0"
                                                            title="Baixar foto"
                                                        >
                                                            <Download className="h-3 w-3" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <p>Nenhum item encontrado neste pedido.</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Modal de Visualização da Foto */}
            {selectedPhoto && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl max-h-full overflow-hidden">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-semibold">Visualização da Foto</h3>
                            <Button
                                variant="outline"
                                onClick={() => setSelectedPhoto(null)}
                            >
                                Fechar
                            </Button>
                        </div>
                        <div className="p-4">
                            <img
                                src={selectedPhoto}
                                alt="Visualização"
                                className="max-w-full max-h-96 object-contain mx-auto"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
